import { StrictMode, Suspense, lazy, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, Outlet, RouterProvider, useLocation } from 'react-router';
import { LandingPage } from './pages/LandingPage.js';
import { LegalPage } from './pages/LegalPage.js';
import { SignInPage } from './pages/SignInPage.js';
import { DeleteAccountPage } from './pages/DeleteAccountPage.js';
import { RouteMethodologyPage } from './pages/RouteMethodologyPage.js';
import { Seo } from './components/Seo.js';
import { trackEvent, trackEventOnce, trackMarketingPage } from './lib/analytics.js';
import '@fontsource-variable/bricolage-grotesque/wght.css';
import '@fontsource-variable/fraunces/soft.css';
import 'maplibre-gl/dist/maplibre-gl.css';
import './styles.css';

const marketingPaths = new Set([
  '/',
  '/bikepacking-trip-planner',
  '/live-bikepacking-tracker',
  '/share-bikepacking-trip',
  '/offline-bikepacking-tracker',
  '/bikepacking-gpx-route-planner',
  '/bikepacking-distance-per-day',
  '/weekend-bikepacking-trip-planner',
  '/bikepacking-app',
  '/route-methodology',
]);

function RouteRoot() {
  const location = useLocation();
  useEffect(() => {
    const pathname =
      location.pathname.length > 1 ? location.pathname.replace(/\/+$/, '') : location.pathname;
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    if (marketingPaths.has(pathname)) trackMarketingPage(pathname);
    if (pathname === '/start') trackEventOnce('onboarding-started', 'onboarding_started');
  }, [location.pathname]);
  useEffect(() => {
    function trackStartClick(event: MouseEvent) {
      const anchor = (event.target as Element | null)?.closest<HTMLAnchorElement>('a[href]');
      if (!anchor) return;
      try {
        if (new URL(anchor.href).pathname === '/start') {
          trackEvent('plan_clicked', {
            cta: anchor.textContent?.trim() || 'Plan a ride',
            page: window.location.pathname,
          });
        }
      } catch {
        // Ignore malformed or browser-managed links.
      }
    }
    document.addEventListener('click', trackStartClick, true);
    return () => document.removeEventListener('click', trackStartClick, true);
  }, []);
  useEffect(() => {
    void import('./lib/webVitals.js').then(({ startWebVitals }) => startWebVitals());
  }, []);
  return (
    <>
      <Seo pathname={location.pathname} />
      <Outlet />
    </>
  );
}

const FollowerPage = lazy(() =>
  import('./pages/FollowerPage.js').then((module) => ({ default: module.FollowerPage })),
);
const IntentLandingPage = lazy(() =>
  import('./pages/IntentLandingPage.js').then((module) => ({
    default: module.IntentLandingPage,
  })),
);
const AppLayout = lazy(() =>
  import('./pages/AppPages.js').then((module) => ({ default: module.AppLayout })),
);
const DashboardPage = lazy(() =>
  import('./pages/AppPages.js').then((module) => ({ default: module.DashboardPage })),
);
const NewTripPage = lazy(() =>
  import('./pages/AppPages.js').then((module) => ({ default: module.NewTripPage })),
);
const TripPage = lazy(() =>
  import('./pages/TripPage.js').then((module) => ({ default: module.TripPage })),
);
const TripEditPage = lazy(() =>
  import('./pages/TripPage.js').then((module) => ({ default: module.TripEditPage })),
);
const MembershipPage = lazy(() =>
  import('./pages/MembershipPage.js').then((module) => ({ default: module.MembershipPage })),
);
const DataPage = lazy(() =>
  import('./pages/DataPage.js').then((module) => ({ default: module.DataPage })),
);
const StartTripPage = lazy(() =>
  import('./pages/StartTripPage.js').then((module) => ({ default: module.StartTripPage })),
);

function RouteLoading() {
  return (
    <main className="follower-gate" aria-busy="true">
      <section>
        <p className="eyebrow">Bikepacking Live</p>
        <h1>Loading your journey…</h1>
      </section>
    </main>
  );
}

function FollowerRoute() {
  return (
    <Suspense
      fallback={
        <main className="follower-gate">
          <section>
            <p className="eyebrow">Bikepacking Live</p>
            <h1>Opening private trip…</h1>
          </section>
        </main>
      }
    >
      <FollowerPage />
    </Suspense>
  );
}

const router = createBrowserRouter([
  {
    element: <RouteRoot />,
    children: [
      { path: '/', element: <LandingPage /> },
      {
        path: '/bikepacking-trip-planner',
        element: <IntentLandingPage slug="bikepacking-trip-planner" />,
      },
      {
        path: '/live-bikepacking-tracker',
        element: <IntentLandingPage slug="live-bikepacking-tracker" />,
      },
      {
        path: '/share-bikepacking-trip',
        element: <IntentLandingPage slug="share-bikepacking-trip" />,
      },
      {
        path: '/offline-bikepacking-tracker',
        element: <IntentLandingPage slug="offline-bikepacking-tracker" />,
      },
      {
        path: '/bikepacking-gpx-route-planner',
        element: <IntentLandingPage slug="bikepacking-gpx-route-planner" />,
      },
      {
        path: '/bikepacking-distance-per-day',
        element: <IntentLandingPage slug="bikepacking-distance-per-day" />,
      },
      {
        path: '/weekend-bikepacking-trip-planner',
        element: <IntentLandingPage slug="weekend-bikepacking-trip-planner" />,
      },
      {
        path: '/bikepacking-app',
        element: <IntentLandingPage slug="bikepacking-app" />,
      },
      { path: '/signin', element: <SignInPage /> },
      { path: '/start', element: <StartTripPage /> },
      { path: '/privacy', element: <LegalPage kind="privacy" /> },
      { path: '/terms', element: <LegalPage kind="terms" /> },
      { path: '/imprint', element: <LegalPage kind="imprint" /> },
      { path: '/delete-account', element: <DeleteAccountPage /> },
      { path: '/route-methodology', element: <RouteMethodologyPage /> },
      { path: '/t/:publicId', element: <FollowerRoute /> },
      {
        path: '/app',
        element: <AppLayout />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'trips/new', element: <NewTripPage /> },
          { path: 'trips/:tripId', element: <TripPage /> },
          { path: 'trips/:tripId/edit', element: <TripEditPage /> },
          { path: 'membership', element: <MembershipPage /> },
          { path: 'data', element: <DataPage /> },
        ],
      },
    ],
  },
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Suspense fallback={<RouteLoading />}>
      <RouterProvider router={router} />
    </Suspense>
  </StrictMode>,
);
